import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { Box, Card, Dialog, Image, Typography } from '@packages/shared';
import { useState } from 'react';
import { ServiceLayer } from '@packages/cms-components';
import { EcoLabelItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/EcoLabelItemFragmentFragmentDoc';
import { useServiceLayerTracking } from '../Tracking/hooks/useServiceLayerTracking';

/* GraphQL */ `
  fragment EcoLabelItemFragment on EcoLabel {
    iconUrl
    text
    serviceLayerId
    license
  }
`;

export type EcoLabelProps = {
  ecoLabelData: FragmentType<typeof EcoLabelItemFragmentFragmentDoc>;
  /**
   * If `ecoLabelData['text']` is `null`, this fallback is used instead.
   */
  fallbackHeadline?: string;
};

/**
 * <p>This component renders one eco-attribute of a product</p>
 * <i>(e.G.: "Bio Baumwolle", "Grüner Knopf" or "GOTS organic").</i>
 * */
export const EcoLabelItem = ({ fallbackHeadline, ecoLabelData }: EcoLabelProps) => {
  const { iconUrl, license, serviceLayerId, text } = getFragmentData(
    EcoLabelItemFragmentFragmentDoc,
    ecoLabelData,
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { trackServiceLayerClick } = useServiceLayerTracking();

  // if iconUrl, license and text is missing we can not render the component
  // because it would have no content to display
  if (!iconUrl && !license && !text) {
    return null;
  }

  const handleClick = () => {
    trackServiceLayerClick(serviceLayerId!);
    setIsModalOpen(true);
  };

  return (
    <>
      <Card>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'nowrap',
            padding: 1.5,
            cursor: serviceLayerId ? 'pointer' : 'default',
          }}
          {...(serviceLayerId && {
            onClick: handleClick,
            role: 'button',
          })}
        >
          {iconUrl && (
            <Box
              sx={{
                width: '3.25rem',
                height: '3.25rem',
                mr: 2,
                overflow: 'hidden',
                position: 'relative',
                '> img': {
                  objectFit: 'contain',
                },
              }}
            >
              <Image alt={text || ''} src={iconUrl} unoptimized fill />
            </Box>
          )}

          <Box>
            {text && <Typography variant="body2">{text}</Typography>}
            {license && (
              <Typography variant="body3" sx={{ color: 'text.darkTransparent' }}>
                {license}
              </Typography>
            )}
          </Box>
        </Box>
      </Card>
      {serviceLayerId && (
        <Dialog
          open={isModalOpen}
          handleClose={() => setIsModalOpen(false)}
          title={text || fallbackHeadline || ''}
          dialogContent={
            <Box sx={{ p: 2 }}>
              <ServiceLayer serviceLayerId={serviceLayerId} />
            </Box>
          }
          displayTrackingProps={{ category: 'dv-service-layer-eco-label-item' }}
        />
      )}
    </>
  );
};
